import { useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { QcFailureRow, QcDashboard } from "@/lib/manufacturing/qcDashboardClient";
import { queueBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";
import { requestWorkspaceMode } from "@/lib/workspaceModeNavigation";
import { QcListPager } from "./qcListControls";
import { QcPhotoViewer } from "./QcPhotoViewer";
import { QcReportDeleteButton } from "./QcReportDeleteButton";

function when(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function ViewPhotos({ urls, title }: { urls: string[]; title: string }) {
  const [menu, setMenu] = useState(false);
  const [viewer, setViewer] = useState<number | null>(null);
  if (urls.length === 0) return <span>—</span>;
  return (
    <div className="relative">
      <button type="button" className={ghostBtnClass} onClick={() => setMenu((open) => !open)}>
        View Photos
      </button>
      {menu ? (
        <div className="absolute right-0 z-20 mt-1 min-w-36 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          {urls.map((url, index) => (
            <button
              key={url}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-zinc-100 dark:hover:bg-zinc-800"
              onClick={() => {
                setViewer(index);
                setMenu(false);
              }}
            >
              Photo {index + 1}
            </button>
          ))}
        </div>
      ) : null}
      {viewer != null ? (
        <QcPhotoViewer urls={urls} index={viewer} title={title} onClose={() => setViewer(null)} />
      ) : null}
    </div>
  );
}

function openReport(sessionId: string) {
  queueBuilderAssistantSession(sessionId);
  requestWorkspaceMode({ mode: "mfgBuilderAssistant" });
}

export function QcDashboardFailureTable({
  rows,
  dashboard,
  onPage,
  onDeleted,
}: {
  rows: QcFailureRow[];
  dashboard: Pick<QcDashboard, "failure_total" | "failure_page" | "failure_page_size">;
  onPage: (page: number) => void;
  onDeleted: () => void;
}) {
  const pages = Math.max(1, Math.ceil(dashboard.failure_total / dashboard.failure_page_size));
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm text-gray-900 dark:text-zinc-100">
          <thead>
            <tr>
              <th className="py-2 pr-3 font-semibold">Group</th>
              <th className="py-2 pr-3 font-semibold">Item</th>
              <th className="py-2 pr-3 font-semibold">Serial</th>
              <th className="py-2 pr-3 font-semibold">Model</th>
              <th className="py-2 pr-3 font-semibold">
                <span className="inline-flex items-center gap-1">
                  Size
                  <InlineHelp glyph="!" label="How size is read">
                    Size is read from the model text when it looks like 12x20. It is not stored as its own field yet.
                  </InlineHelp>
                </span>
              </th>
              <th className="py-2 pr-3 font-semibold">Inspector</th>
              <th className="py-2 pr-3 font-semibold">When</th>
              <th className="py-2 pr-3 font-semibold">Status</th>
              <th className="py-2 pr-3 font-semibold">Photos</th>
              <th className="py-2 font-semibold">Report</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-200 dark:border-zinc-700">
                <td className="py-2 pr-3">{row.group}</td>
                <td className="py-2 pr-3">
                  <button type="button" className="text-left font-medium text-indigo-700 dark:text-indigo-300" onClick={() => openReport(row.session_id)}>
                    {row.item}
                  </button>
                </td>
                <td className="py-2 pr-3">{row.serial || "—"}</td>
                <td className="py-2 pr-3">{row.model || "—"}</td>
                <td className="py-2 pr-3">{row.size || "—"}</td>
                <td className="py-2 pr-3">{row.inspector}</td>
                <td className="py-2 pr-3">{when(row.observed_at)}</td>
                <td className="py-2 pr-3">{row.open ? "Open" : "Corrected"}</td>
                <td className="py-2 pr-3">
                  <ViewPhotos urls={row.photos} title={row.item} />
                </td>
                <td className="w-48 py-2 align-top whitespace-normal">
                  <QcReportDeleteButton
                    sessionId={row.session_id}
                    label={row.serial || "this QC report"}
                    onDeleted={onDeleted}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 ? <p className="text-sm text-gray-900 dark:text-zinc-100">No QC failures yet.</p> : null}
      <QcListPager page={dashboard.failure_page} pages={pages} onPage={onPage} />
    </div>
  );
}
